import React, { useState } from 'react';
import { X, Send, Star } from 'lucide-react';
import { sendSupportRequest } from '../services/api';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose, onLogoutConfirm }) => {
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [improvements, setImprovements] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = rating, 2 = follow-up

  const handleStarClick = (value) => {
    setRating(value);
    setStep(2); // Move to follow-up
  };

  const handleSkip = () => {
    onClose();
    onLogoutConfirm(); // Proceed with logout
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Build feedback message
      const feedbackMessage = `
Session Rating: ${rating}/5 stars

${improvements.trim() ? `Feedback:\n${improvements.trim()}` : 'No additional feedback provided'}

---
Auto-generated feedback from logout
      `.trim();

      // Send via existing support system
      await sendSupportRequest({
        subject: `⭐ Session Feedback: ${rating}/5 Stars`,
        message: feedbackMessage,
        image_url: null
      });

      // Close modal and proceed with logout
      onClose();
      onLogoutConfirm();
    } catch (error) {
      console.error('Failed to send feedback:', error);
      // Still allow logout even if feedback fails
      onClose();
      onLogoutConfirm();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="feedback-modal-overlay" onClick={handleSkip} />
      
      <div className="feedback-modal">
        {/* Header */}
        <div className="feedback-modal-header">
          <h3 className="feedback-modal-title">
            {step === 1 ? 'Before you go...' : 'Thanks for rating!'}
          </h3>
          <button onClick={handleSkip} className="feedback-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="feedback-modal-body">
          {step === 1 ? (
            <>
              <p className="feedback-prompt">How was your session today?</p>
              
              <div className="feedback-stars">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    className="feedback-star-btn"
                    onClick={() => handleStarClick(value)}
                    onMouseEnter={() => setHoveredStar(value)}
                    onMouseLeave={() => setHoveredStar(0)}
                  >
                    <Star
                      size={48}
                      fill={(hoveredStar || rating) >= value ? '#fbbf24' : 'none'}
                      color={(hoveredStar || rating) >= value ? '#fbbf24' : '#d1d5db'}
                      strokeWidth={2}
                    />
                  </button>
                ))}
              </div>

              <p className="feedback-hint">Click a star to rate</p>
            </>
          ) : (
            <>
              <div className="feedback-rating-display">
                <div className="feedback-stars-small">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Star
                      key={value}
                      size={24}
                      fill={rating >= value ? '#fbbf24' : 'none'}
                      color={rating >= value ? '#fbbf24' : '#d1d5db'}
                    />
                  ))}
                </div>
              </div>

              {rating <= 3 ? (
                <p className="feedback-prompt">What could we improve?</p>
              ) : (
                <p className="feedback-prompt">What did you love? (optional)</p>
              )}

              <textarea
                className="feedback-textarea"
                placeholder={
                  rating <= 3
                    ? "Tell us what went wrong or what we can do better..."
                    : "Share what you enjoyed or any suggestions..."
                }
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                rows={4}
                disabled={loading}
              />

              <div className="feedback-actions">
                <button
                  onClick={handleSkip}
                  className="feedback-btn feedback-btn-skip"
                  disabled={loading}
                >
                  Skip
                </button>
                <button
                  onClick={handleSubmit}
                  className="feedback-btn feedback-btn-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="feedback-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit & Logout
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {step === 1 && (
          <div className="feedback-modal-footer">
            <button onClick={handleSkip} className="feedback-skip-link">
              Skip and logout
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FeedbackModal;
